/**
 * WHAT: Admin "Secret Settings" screen. Lists each application secret DAMA needs (metadata
 *       DB password, Django key, email/SMTP, Azure AD, Oracle, Anthropic), shows which
 *       secret store + path each resolves from and whether it is currently RESOLVED from the
 *       store — without ever displaying a secret value. DEV_ADMIN can push/rotate a value
 *       into the store from here.
 *
 * WHY:  DAMA keeps NO secret values in any config file (vault-only). This page lets an admin
 *       VERIFY in the browser that every secret genuinely resolves from the configured store
 *       (provider Vault/Conjur/Azure/AWS), and ROTATE a secret post-install without shell
 *       access — the same provider-backed write the installer's bootstrap_secrets.sh performs.
 *       Values are write-only: pasted in, pushed to the store, never read back to the UI.
 */
import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../services/api';
import API_CONFIG from '../config/api.config';

const SecretSettings = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pushKey, setPushKey] = useState(null);   // which secret's push form is open
  const [pushValue, setPushValue] = useState('');
  const [pushing, setPushing] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(API_CONFIG.getDatabasesEndpoint('config/secrets/'));
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load secret settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openPush = (key) => { setPushKey(key); setPushValue(''); setNotice(null); };
  const cancelPush = () => { setPushKey(null); setPushValue(''); };

  const submitPush = async (entry) => {
    if (!pushValue) return;
    setPushing(true);
    setNotice(null);
    try {
      await apiClient.post(API_CONFIG.getDatabasesEndpoint('config/secrets/push/'),
        { key: entry.key, value: pushValue });
      setNotice({ type: 'success', text: `Stored "${entry.label}" in the secret store.` });
      setPushKey(null);
      setPushValue('');
      await load();  // reflect new resolved state
    } catch (err) {
      const msg = err.response?.status === 403
        ? 'Forbidden: only DEV_ADMIN can push secrets.'
        : (err.response?.data?.error || 'Failed to push secret');
      setNotice({ type: 'danger', text: msg });
    } finally {
      setPushing(false);
    }
  };

  if (loading) {
    return <div className="p-4"><i className="fas fa-spinner fa-spin me-2" />Loading secret settings…</div>;
  }
  if (error) {
    return <div className="alert alert-danger m-4"><i className="fas fa-exclamation-triangle me-2" />{error}</div>;
  }

  const secrets = data?.secrets || [];
  const resolvedCount = secrets.filter(s => s.in_store).length;
  const requiredMissing = secrets.filter(s => s.required && !s.in_store).length;

  return (
    <div className="container-fluid py-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="mb-0"><i className="fas fa-key me-2" />Secret Settings</h4>
        <button className="btn btn-sm btn-outline-secondary" onClick={load}>
          <i className="fas fa-sync-alt me-1" />Refresh
        </button>
      </div>

      <div className="alert alert-info py-2">
        <i className="fas fa-info-circle me-2" />
        DAMA stores secret <strong>values</strong> only in your secret store — never in a config file.
        This page verifies each secret resolves from the store and lets a DEV_ADMIN rotate one.
        Values are never displayed.
      </div>

      <div className="d-flex gap-4 mb-3">
        <div><small className="text-muted d-block">Provider</small>
          <span className="fw-semibold text-info">{data?.provider || 'unknown'}</span>
          {data?.provider_enabled
            ? <span className="text-success ms-2"><i className="fas fa-circle fa-xs me-1" />enabled</span>
            : <span className="text-danger ms-2"><i className="fas fa-circle fa-xs me-1" />disabled</span>}
        </div>
        <div><small className="text-muted d-block">Resolved from store</small>
          <span className="fw-semibold">{resolvedCount} / {secrets.length}</span></div>
        <div><small className="text-muted d-block">Required missing</small>
          {requiredMissing > 0
            ? <span className="fw-semibold text-danger">{requiredMissing}</span>
            : <span className="fw-semibold text-success">0</span>}</div>
      </div>

      {notice && (
        <div className={`alert alert-${notice.type} py-2`}>{notice.text}</div>
      )}

      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0">
          <thead className="table-dark">
            <tr>
              <th>Secret</th>
              <th>Store path</th>
              <th>Field</th>
              <th>Required</th>
              <th>Resolved</th>
              <th className="text-end">Action</th>
            </tr>
          </thead>
          <tbody>
            {secrets.map(s => (
              <React.Fragment key={s.key}>
                <tr>
                  <td className="fw-semibold">{s.label}</td>
                  <td><code>{s.path}</code></td>
                  <td><code>{s.field}</code></td>
                  <td>{s.required
                    ? <span className="text-warning">Required</span>
                    : <span className="text-muted">Optional</span>}</td>
                  <td>{s.in_store
                    ? <span className="text-success"><i className="fas fa-circle fa-xs me-1" />vault</span>
                    : <span className={s.required ? 'text-danger' : 'text-muted'}>
                        <i className="fas fa-circle fa-xs me-1" />missing</span>}</td>
                  <td className="text-end">
                    <button className="btn btn-sm btn-outline-primary"
                      onClick={() => (pushKey === s.key ? cancelPush() : openPush(s.key))}>
                      <i className="fas fa-upload me-1" />{s.in_store ? 'Rotate' : 'Set'}
                    </button>
                  </td>
                </tr>
                {pushKey === s.key && (
                  <tr className="table-light">
                    <td colSpan={6}>
                      <div className="d-flex align-items-center gap-2 flex-wrap p-2">
                        <span className="text-muted small">
                          Paste a value to write to <code>{s.path}</code>. It is sent to the store and never shown again.
                        </span>
                        <input type="password" autoComplete="new-password"
                          className="form-control form-control-sm" style={{ maxWidth: 320 }}
                          placeholder={`New value for ${s.label}`}
                          value={pushValue} onChange={e => setPushValue(e.target.value)} />
                        <button className="btn btn-sm btn-primary" disabled={!pushValue || pushing}
                          onClick={() => submitPush(s)}>
                          {pushing ? <><i className="fas fa-spinner fa-spin me-1" />Pushing…</>
                                   : <><i className="fas fa-save me-1" />Push to store</>}
                        </button>
                        <button className="btn btn-sm btn-outline-secondary" onClick={cancelPush}>Cancel</button>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SecretSettings;
